/**
 * BillingSettings
 *
 * Admin screen to manage global billing defaults:
 *   - Default melting loss %
 *   - Purity factors per karat (22K, 18K, etc.)
 *   - Stone deduction presets (label + %)
 *
 * Route: 'billing-settings' (Admin section)
 */

import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Plus, Trash2 } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const DEFAULT_PURITY = { '24K': 1.0, '22K': 0.916, '18K': 0.75, '14K': 0.585, '9K': 0.375 };
const DEFAULT_PRESETS = [
  { label: 'None (plain gold)',  pct: 0  },
  { label: 'Minimal (1–2 gems)', pct: 5  },
  { label: 'Medium set',         pct: 15 },
  { label: 'Heavy stone set',    pct: 30 },
  { label: 'Kundan / Jadau',     pct: 50 },
];

export default function BillingSettings() {
  const [loading, setSaving]          = useState(false);
  const [fetching, setFetching]       = useState(true);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState('');

  // Form state
  const [meltingLoss, setMeltingLoss]     = useState('3');
  const [purityRows, setPurityRows]       = useState(
    Object.entries(DEFAULT_PURITY).map(([k, v]) => ({ key: k, factor: String(v) }))
  );
  const [newPurityKey, setNewPurityKey]   = useState('');
  const [newPurityVal, setNewPurityVal]   = useState('');
  const [presets, setPresets]             = useState(DEFAULT_PRESETS.map(p => ({ ...p })));

  // Load settings
  useEffect(() => {
    setFetching(true);
    fetch(`${API_BASE}/billing-settings`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;
        const s = data.data;
        setMeltingLoss(String(s.default_melting_loss_pct ?? 3));
        if (s.purity_factors) {
          setPurityRows(Object.entries(s.purity_factors).map(([k, v]) => ({ key: k, factor: String(v) })));
        }
        if (Array.isArray(s.stone_deduction_presets)) {
          setPresets(s.stone_deduction_presets.map(p => ({ ...p })));
        }
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setFetching(false));
  }, []);

  // Save
  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');

    const purityFactors = {};
    for (const row of purityRows) {
      const k = row.key.trim();
      const v = parseFloat(row.factor);
      if (!k) { setError('Purity label cannot be empty'); setSaving(false); return; }
      if (isNaN(v) || v <= 0 || v > 1) { setError(`Purity factor for "${k}" must be between 0 and 1`); setSaving(false); return; }
      purityFactors[k] = v;
    }

    for (const p of presets) {
      if (!p.label?.trim()) { setError('Preset label cannot be empty'); setSaving(false); return; }
      if (typeof p.pct !== 'number' || p.pct < 0 || p.pct >= 100) {
        setError(`Preset "${p.label}": pct must be 0–99`); setSaving(false); return;
      }
    }

    try {
      const res = await fetch(`${API_BASE}/billing-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          default_melting_loss_pct: parseFloat(meltingLoss),
          purity_factors: purityFactors,
          stone_deduction_presets: presets,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Purity row helpers
  const updatePurityRow = (idx, patch) =>
    setPurityRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const removePurityRow = (idx) =>
    setPurityRows(rows => rows.filter((_, i) => i !== idx));

  const addPurityRow = () => {
    const k = newPurityKey.trim();
    const v = newPurityVal.trim();
    if (!k || !v) return;
    if (purityRows.some(r => r.key === k)) { setError(`Purity "${k}" already exists`); return; }
    setPurityRows(rows => [...rows, { key: k, factor: v }]);
    setNewPurityKey('');
    setNewPurityVal('');
  };

  // Preset helpers
  const updatePreset = (idx, patch) =>
    setPresets(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const removePreset = (idx) =>
    setPresets(rows => rows.filter((_, i) => i !== idx));

  const addPreset = () =>
    setPresets(rows => [...rows, { label: '', pct: 0 }]);

  if (fetching) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <RefreshCw size={16} className="animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.ADMINISTRATION.BILLING_SETTINGS} pageName="Billing Settings" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-slate-500 mt-0.5">
            Global defaults for old gold exchange calculations. Overridable at billing time.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
          Save Settings
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
      {saved && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
          Settings saved successfully.
        </div>
      )}

      {/* ── Melting Loss ───────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Default Melting Loss</h2>
        <p className="text-xs text-slate-500 mb-3">
          Applied to net gold weight during old gold exchange. Staff can override per transaction.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number" min="0" max="20" step="0.5"
            value={meltingLoss}
            onChange={e => setMeltingLoss(e.target.value)}
            className="w-28 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <span className="text-sm text-slate-500">%</span>
          <span className="text-xs text-slate-400">(typical range: 1–5 %)</span>
        </div>
      </section>

      {/* ── Purity Factors ─────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">Gold Purity Factors</h2>
        <p className="text-xs text-slate-500 mb-3">
          Factor = pure gold content per gram (0–1). E.g. 22K = 0.9160 means 91.60% pure.
        </p>
        <div className="space-y-2">
          {purityRows.map((row, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <input
                type="text"
                value={row.key}
                onChange={e => updatePurityRow(idx, { key: e.target.value })}
                className="w-24 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                placeholder="22K"
              />
              <span className="text-slate-400 text-sm">=</span>
              <input
                type="number" min="0" max="1" step="0.0001"
                value={row.factor}
                onChange={e => updatePurityRow(idx, { factor: e.target.value })}
                className="w-28 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="0.9160"
              />
              <span className="text-xs text-slate-400 w-20">
                = {((parseFloat(row.factor) || 0) * 100).toFixed(2)}% pure
              </span>
              <button
                onClick={() => removePurityRow(idx)}
                className="p-1 text-slate-400 hover:text-red-500 rounded"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {/* Add row */}
          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
            <input
              type="text"
              value={newPurityKey}
              onChange={e => setNewPurityKey(e.target.value)}
              className="w-24 text-sm border border-dashed border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              placeholder="Label"
            />
            <span className="text-slate-400 text-sm">=</span>
            <input
              type="number" min="0" max="1" step="0.0001"
              value={newPurityVal}
              onChange={e => setNewPurityVal(e.target.value)}
              className="w-28 text-sm border border-dashed border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              placeholder="0.0000"
            />
            <button
              onClick={addPurityRow}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-3 py-2 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50"
            >
              <Plus size={12} /> Add
            </button>
          </div>
        </div>
      </section>

      {/* ── Stone Deduction Presets ────────────────────────── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">Stone Deduction Presets</h2>
        <p className="text-xs text-slate-500 mb-3">
          Quick-select options shown when evaluating old gold with stones. No stone credit is given — stones are kept by the shop.
        </p>
        <div className="space-y-2">
          {presets.map((preset, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <input
                type="text"
                value={preset.label}
                onChange={e => updatePreset(idx, { label: e.target.value })}
                className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Label"
              />
              <input
                type="number" min="0" max="99" step="1"
                value={preset.pct}
                onChange={e => updatePreset(idx, { pct: parseFloat(e.target.value) || 0 })}
                className="w-20 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <span className="text-slate-500 text-sm">%</span>
              <button
                onClick={() => removePreset(idx)}
                className="p-1 text-slate-400 hover:text-red-500 rounded"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button
            onClick={addPreset}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 px-3 py-2 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 mt-1"
          >
            <Plus size={12} /> Add Preset
          </button>
        </div>
      </section>
    </div>
  );
}
